import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, CheckCircle, ArrowLeft } from 'lucide-react';
import { Layout } from '@/components/layout';
import { Button } from '@/components/ui/button';
import SEO from '@/components/shared/SEO';

// Flooring Solutions
const flooringSolutions = [
  {
    id: 'lvp',
    title: 'Luxury Vinyl Plank (LVP)',
    description: 'Realistic wood-look planks with superior durability and water resistance for commercial applications.',
    image: '/Assets/images/products/7in/7x48_hero-img.jpg',
    features: ['Waterproof core', 'Realistic wood textures', 'Easy installation', '20+ year commercial warranty']
  },
  {
    id: 'lvt',
    title: 'Luxury Vinyl Tile (LVT)',
    description: 'Stone and abstract patterns that deliver stunning aesthetics with commercial-grade performance.',
    image: '/Assets/images/products/18in/18x18_hero-img.jpg',
    features: ['Stone & abstract designs', 'High traffic rated', 'Low maintenance', 'Seamless appearance']
  },
  {
    id: 'carpet-tile',
    title: 'Custom Carpet Tile',
    description: 'Modular carpet solutions with custom colors, patterns, and branding integration capabilities.',
    image: '/Assets/images/products/Kensington/Kensington_hero-img.jpg',
    features: ['Custom color matching', 'Pattern flexibility', 'Easy replacement', 'Sound absorption']
  },
  {
    id: 'rubber',
    title: 'Rubber Flooring',
    description: 'Durable rubber solutions for high-impact areas requiring safety and resilience.',
    image: '/Assets/images/products/SafeStep-Plus/SafeStepPlus_hero-img.jpg',
    features: ['Impact resistance', 'Slip resistant', 'Sound dampening', 'Eco-friendly options']
  },
  {
    id: 'grid-systems',
    title: 'Grid Entry Systems',
    description: 'Engineered entry systems that combine functionality with custom branding opportunities.',
    image: '/Assets/images/products/Snap-Trax/SnapTrax_hero-img.jpg',
    features: ['Modular design', 'Custom inserts', 'ADA compliant', 'Heavy-duty construction']
  },
  {
    id: 'broadloom',
    title: 'Broadloom Carpet',
    description: 'Wall-to-wall carpet solutions with custom color and pattern capabilities.',
    image: '/Assets/images/products/portico-nop-RG/portico-nop-RG_hero-img.jpg',
    features: ['Seamless installation', 'Custom designs', 'Wide roll widths', 'Performance backing']
  }
];

// Benefits
const benefits = [
  {
    title: 'Design Freedom',
    description: 'Create unique flooring that perfectly matches your brand identity and design vision.'
  },
  {
    title: 'Performance Engineering',
    description: 'Custom specifications ensure your flooring meets the exact demands of your environment.'
  },
  {
    title: 'Color Matching',
    description: 'Precise color matching to your brand standards across all flooring products.'
  },
  {
    title: 'Sustainable Options',
    description: 'Eco-friendly materials and LEED-contributing products available.'
  }
];

const CustomFlooringSolutionsPage = () => {
  return (
    <Layout>
      <SEO 
        title="Custom Flooring Solutions"
        description="Explore our range of custom flooring solutions including LVP, LVT, broadloom carpet, and specialty flooring. Tailored to your commercial space requirements."
        canonicalUrl="/custom/flooring-solutions"
      />
      {/* Hero Section */}
      <section className="relative bg-primary-bg text-white py-20" data-testid="flooring-solutions-hero">
        <div className="container-portico">
          <Link to="/custom" className="inline-flex items-center text-white/80 hover:text-white mb-6 transition-colors">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Custom Hub
          </Link>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-3xl"
          >
            <h1 className="font-heading text-4xl md:text-5xl font-bold mb-6">
              Custom Flooring Solutions
            </h1>
            <p className="text-xl text-white/90 mb-8">
              Tailored flooring systems engineered to meet your specific performance requirements, 
              design aesthetic, and brand identity.
            </p>
            <div className="flex flex-wrap gap-4">
              <Button 
                size="lg" 
                className="bg-secondary hover:bg-secondary-light"
                onClick={() => document.getElementById('consultation-cta').scrollIntoView({ behavior: 'smooth' })}
              >
                Start Your Project
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
              <Link to="/custom/portfolio?category=flooring">
                <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-primary">
                  View Projects
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Flooring Solutions Grid */}
      <section className="section-padding bg-background">
        <div className="container-portico">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4">
              Flooring Types
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              From luxury vinyl to custom carpet, we offer comprehensive flooring solutions 
              for every commercial application.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {flooringSolutions.map((solution, index) => (
              <motion.div
                key={solution.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-card rounded-xl overflow-hidden shadow-lg border border-border hover:shadow-xl transition-shadow"
              >
                <div className="h-48 overflow-hidden">
                  <img
                    src={solution.image}
                    alt={solution.title}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="p-6">
                  <h3 className="font-heading font-bold text-lg mb-2">{solution.title}</h3>
                  <p className="text-sm text-muted-foreground mb-4">{solution.description}</p>
                  <ul className="space-y-1">
                    {solution.features.map((feature) => (
                      <li key={feature} className="flex items-center gap-2 text-xs">
                        <CheckCircle className="w-3 h-3 text-secondary flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="section-padding bg-muted">
        <div className="container-portico">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4">
              Why Custom Flooring?
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Custom flooring solutions deliver benefits that off-the-shelf products simply can't match.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((benefit, index) => (
              <motion.div
                key={benefit.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-card p-6 rounded-xl border border-border text-center"
              >
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-6 h-6 text-primary" />
                </div>
                <h3 className="font-heading font-semibold text-lg mb-2">{benefit.title}</h3>
                <p className="text-sm text-muted-foreground">{benefit.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Overview */}
      <section className="section-padding bg-background">
        <div className="container-portico">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="font-heading text-3xl md:text-4xl font-bold mb-6">
                Our Custom Process
              </h2>
              <p className="text-lg text-muted-foreground mb-6">
                From initial consultation to final installation, our team guides you through 
                every step of creating your perfect custom flooring solution.
              </p>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-primary text-white flex items-center justify-center flex-shrink-0 font-bold text-sm">1</div>
                  <div>
                    <span className="font-semibold">Consultation & Discovery</span>
                    <p className="text-sm text-muted-foreground">We learn about your space, requirements, and design vision</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-primary text-white flex items-center justify-center flex-shrink-0 font-bold text-sm">2</div>
                  <div>
                    <span className="font-semibold">Design & Specification</span>
                    <p className="text-sm text-muted-foreground">Custom designs and material specifications developed</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-primary text-white flex items-center justify-center flex-shrink-0 font-bold text-sm">3</div>
                  <div>
                    <span className="font-semibold">Sample Approval</span>
                    <p className="text-sm text-muted-foreground">Physical samples provided for final approval</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-primary text-white flex items-center justify-center flex-shrink-0 font-bold text-sm">4</div>
                  <div>
                    <span className="font-semibold">Production & Installation</span>
                    <p className="text-sm text-muted-foreground">Manufacturing and professional installation</p>
                  </div>
                </li>
              </ul>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <img
                src="/Assets/images/industries/corporate-offices.jpg"
                alt="Custom flooring process"
                className="rounded-xl shadow-xl"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section id="consultation-cta" className="section-padding bg-primary-bg text-white">
        <div className="container-portico text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-2xl mx-auto"
          >
            <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4">
              Let's Create Something Unique
            </h2>
            <p className="text-lg text-white/80 mb-8">
              Ready to explore custom flooring options for your project? 
              Our design team is here to help bring your vision to life.
            </p>
            <Link to="/custom#consultation-form">
              <Button size="lg" className="bg-secondary hover:bg-secondary-light">
                Start Your Project
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>
    </Layout>
  );
};

export default CustomFlooringSolutionsPage;
